// Reference piano: note utilities + sound (Web Audio; acoustic samples with synth fallback).
// Faithful port of the Mobile App's src/piano.ts.
import { resolveFileUrl } from "./api";

export const KEY_MIN = 36; // Dó2
export const KEY_MAX = 72; // Dó5

const PT = ["Dó", "Dó♯", "Ré", "Ré♯", "Mi", "Fá", "Fá♯", "Sol", "Sol♯", "Lá", "Lá♯", "Si"];
const INTL = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export const isBlackKey = (midi) => [1, 3, 6, 8, 10].includes(((midi % 12) + 12) % 12);
export const octaveOf = (midi) => Math.floor(midi / 12) - 1;
export const notePt = (midi) => `${PT[((midi % 12) + 12) % 12]}${octaveOf(midi)}`;
export const noteIntl = (midi) => `${INTL[((midi % 12) + 12) % 12]}${octaveOf(midi)}`;

/** "E4" | "F#3" -> MIDI; null if invalid. */
export function midiFromIntl(name) {
  if (!name) return null;
  const m = /^([A-G])(#?)(-?\d)$/.exec(String(name).trim());
  if (!m) return null;
  const idx = INTL.indexOf(`${m[1]}${m[2]}`);
  return idx < 0 ? null : (Number(m[3]) + 1) * 12 + idx;
}

const freqOf = (midi) => 440 * 2 ** ((midi - 69) / 12);
const sampleUrl = (midi) => resolveFileUrl(`/piano/${midi}.wav`);

export const noteLetter = (midi) => INTL[((midi % 12) + 12) % 12];

function getCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  const ctx = window.__ulcPianoCtx || (window.__ulcPianoCtx = new AC({ latencyHint: "interactive" }));
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// Decoded buffers kept synchronously so a key press starts the sample with no await.
const ready = new Map();
const bufferCache = new Map();
function loadBuffer(ctx, midi) {
  let p = bufferCache.get(midi);
  if (!p) {
    p = fetch(sampleUrl(midi))
      .then((r) => { if (!r.ok) throw new Error("no sample"); return r.arrayBuffer(); })
      .then((ab) => new Promise((res, rej) => ctx.decodeAudioData(ab, res, rej)))
      .then((buf) => { ready.set(midi, buf); return buf; });
    p.catch(() => bufferCache.delete(midi));
    bufferCache.set(midi, p);
  }
  return p;
}

const RELEASE = 0.07; // short, natural damper release (time constant, s)

/** Sustained synth voice (fallback when a sample is unavailable). */
function synthVoice(ctx, midi, out) {
  const now = ctx.currentTime;
  const f = freqOf(midi);
  const oscs = [[1, 1], [2, 0.45], [3, 0.2], [4, 0.1]].map(([k, a]) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = f * k;
    g.gain.setValueAtTime(a, now);
    g.gain.setTargetAtTime(a * 0.35, now + 0.02, 0.6);
    osc.connect(g).connect(out);
    osc.start(now);
    return osc;
  });
  return { stop: (t) => oscs.forEach((o) => o.stop(t)) };
}

export function preloadNotes(midis) {
  const ctx = getCtx();
  if (!ctx) return;
  midis.filter((m) => m != null).forEach((m) => loadBuffer(ctx, m).catch(() => {}));
}

/** Start a note now; it sounds until noteOff(handle). */
export function noteOn(midi) {
  const ctx = getCtx();
  if (!ctx || midi == null) return null;
  const out = ctx.createGain();
  const now = ctx.currentTime;
  out.gain.setValueAtTime(0.0001, now);
  out.gain.exponentialRampToValueAtTime(0.9, now + 0.004);
  out.connect(ctx.destination);
  const h = { ctx, out, src: null, off: false };
  const start = (buf) => {
    if (h.off) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(out);
    src.start();
    h.src = src;
  };
  const buf = ready.get(midi);
  if (buf) start(buf);
  else loadBuffer(ctx, midi).then(start).catch(() => { if (!h.off) h.src = synthVoice(ctx, midi, out); });
  return h;
}

export function noteOff(h) {
  if (!h || h.off) return;
  h.off = true;
  const t = h.ctx.currentTime;
  h.out.gain.cancelScheduledValues(t);
  h.out.gain.setValueAtTime(h.out.gain.value, t);
  h.out.gain.setTargetAtTime(0.0001, t, RELEASE);
  try { h.src?.stop(t + RELEASE * 6); } catch (err) { console.error("Falha ao parar nota", err); }
  setTimeout(() => h.out.disconnect(), (RELEASE * 6 + 0.1) * 1000);
}

/** Play a note for a fixed time (reference buttons). */
export function playNote(midi, duration = 2.2) {
  const h = noteOn(midi);
  if (h) setTimeout(() => noteOff(h), duration * 1000);
}

export const VOICE_SHORT = { Soprano: "S", Contralto: "C", Tenor: "T", Baixo: "B" };
